/**
 * El reporte comercial en pantalla (DISENO §6, PRODUCTO §11): portada con cuatro números, qué encontramos, los que se
 * están yendo, una tarjeta por grupo, la base que cierra y cómo leer los números. Es la misma plantilla que el PDF, con
 * los componentes del repo. La cantidad de grupos es la que vino: nada supone que son cuatro.
 *
 * La portada lleva la marca como la carga el equipo en «Tu marca» (fase 3): su color, su letra y su logo sobre blanco,
 * igual que la vista previa de esa pantalla y que el PDF. Sin «Tu marca», el negro y el nombre. El resto del reporte
 * sigue con los colores de DEENEX: la rampa de los grupos ordena por valor y no depende del color de cada marca.
 */

import type { ReactNode } from "react";
import { MarcaEnLaCabecera, type EstiloDeMarca } from "../../branding/components/Vistas";
import { COLORES, cssDeLetra, esColor, textoSobre } from "../../branding/logic/branding";
import { num } from "../../campana-nueva/utils/format";
import { dias, fecha, hechos, nombreDelGrupo, pesos, textoSeVan, tituloReporte } from "../logic/segmentar";
import type { GrupoDelReporte, Reporte } from "../types";

/** Los grupos ordenados por valor, en una rampa de un solo tono (DISENO, «Tokens de referencia»). */
const RAMPA = ["#2B2179", "#4735BE", "#695EDE", "#9A91EA", "#C3BDF3"];

/** Lo de «Tu marca» que usa la portada. */
export type EstiloDelReporte = Omit<EstiloDeMarca, "marca">;

/** Sin «Tu marca», como el PDF: el primer color de los sugeridos, la letra moderna y el nombre en vez del logo. */
const SIN_TU_MARCA: EstiloDelReporte = { color: COLORES[0].hex, letra: "moderna", logo: null };

function Seccion({ id, titulo, children }: { id: string; titulo: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <h2 id={id} className="font-mono text-xs font-semibold uppercase tracking-wider text-violet-700">
        <span aria-hidden="true">● </span>
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function Dato({ k, v, abajo }: { k: string; v: string; abajo?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs text-slate-500">{k}</p>
      <p className="text-lg font-bold text-slate-900 tabular-nums">{v}</p>
      {abajo && <p className="text-xs text-slate-500">{abajo}</p>}
    </div>
  );
}

/**
 * Lo que vende el reporte, a la vista: qué parte de la base es el grupo y qué parte de la facturación trae (pocos traen
 * mucho). Antes eran dos números en dos lugares de la tarjeta («18% de la base» arriba, «Facturación 38%» en la tabla).
 */
function BaseContraFacturacion({ base, facturacion, color }: { base: number; facturacion: number; color: string }) {
  const filas: [string, number, string][] = [
    ["De tu base", base, "#CBD5E1"],
    ["De tu facturación", facturacion, color],
  ];
  return (
    <div className="space-y-1.5" data-testid="base-vs-facturacion">
      {filas.map(([k, v, c]) => (
        <div key={k} className="grid grid-cols-[minmax(0,8rem)_1fr_3rem] items-center gap-2 text-xs">
          <span className="text-slate-500">{k}</span>
          <span className="h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
            <span className="block h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, v))}%`, background: c }} />
          </span>
          <span className="text-right font-semibold tabular-nums text-slate-900">{num(v)}%</span>
        </div>
      ))}
    </div>
  );
}

function TarjetaGrupo({ g, nombre, indice, color }: { g: GrupoDelReporte; nombre: string | null; indice: number; color: string }) {
  const filas: [string, string][] = [
    ["Compras", num(g.compras)],
    ["Vuelve cada", dias(g.ritmo)],
    ["Sin comprar", dias(g.sinComprar)],
    ["Gastó en total", pesos(g.gasto)],
    ["Ticket típico", pesos(g.ticket)],
  ];
  return (
    <article data-testid={`grupo-${g.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 border-l-[6px] bg-white p-4" style={{ borderLeftColor: color }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-900">
            {nombreDelGrupo({ nombre }, indice)}
            {!nombre && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">sin nombre</span>}
          </h3>
          <p className="text-sm text-slate-500">{g.frase}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold tabular-nums text-slate-900">{num(g.personas)}</p>
          <p className="whitespace-nowrap text-xs text-slate-500">personas</p>
        </div>
      </div>
      <BaseContraFacturacion base={g.pctBase} facturacion={g.pctFacturacion} color={color} />
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        {filas.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-right font-semibold tabular-nums text-slate-900">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="flex gap-4 border-t border-slate-100 pt-2 text-xs text-slate-500">
        <span>
          WhatsApp <b className="tabular-nums text-slate-900">{num(g.conCelular)}</b>
        </span>
        <span>
          Mail <b className="tabular-nums text-slate-900">{num(g.conMail)}</b>
        </span>
      </p>
    </article>
  );
}

export function ReporteGrupos({
  reporte: r,
  nombres,
  estilo = SIN_TU_MARCA,
}: {
  reporte: Reporte;
  nombres: ReadonlyMap<string, string | null>;
  estilo?: EstiloDelReporte;
}) {
  const color = esColor(estilo.color) ? estilo.color : SIN_TU_MARCA.color;
  return (
    <div className="space-y-7" data-testid="reporte-grupos">
      <header className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50" style={{ fontFamily: cssDeLetra(estilo.letra) }}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" style={{ background: color, color: textoSobre(color) }} data-testid="reporte-portada">
          <MarcaEnLaCabecera estilo={{ ...estilo, marca: r.marca }} alto="h-8" />
          <span className="font-mono text-xs uppercase tracking-[0.08em]">Reporte de clientes</span>
        </div>
        <div className="space-y-4 p-5">
          <div className="space-y-1">
            <p className="text-xl sm:text-2xl font-bold text-slate-900">{tituloReporte(r)}</p>
            <p className="text-sm text-slate-500">
              Ventas del {fecha(r.desde)} al {fecha(r.hasta)}
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Dato k="Pedidos" v={num(r.pedidos)} />
            <Dato k="Personas" v={num(r.personas)} />
            <Dato k="Período" v={r.periodo || "—"} />
            <Dato k="Facturación" v={`$${num(r.facturacionMillones)} M`} abajo="en pesos de hoy" />
          </div>
        </div>
      </header>

      <Seccion id="reporte-encontramos" titulo="Qué encontramos">
        <div className="grid gap-3 sm:grid-cols-2">
          {hechos(r).map((h) => (
            <div key={h.texto} className="rounded-xl bg-slate-50 px-4 py-3">
              <p className="text-xl font-bold tabular-nums text-violet-700">{h.valor}</p>
              <p className="text-sm text-slate-600">{h.texto}</p>
            </div>
          ))}
        </div>
      </Seccion>

      {r.seVan && (
        <Seccion id="reporte-se-van" titulo="Los que se están yendo">
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <p className="text-xl font-bold tabular-nums text-violet-700">{num(r.seVan.personas)} clientes</p>
            <p className="text-sm text-slate-600">{textoSeVan(r.seVan)}</p>
          </div>
        </Seccion>
      )}

      <Seccion id="reporte-grupos-titulo" titulo="Los grupos">
        <div className="grid gap-3 lg:grid-cols-2">
          {r.grupos.map((g, i) => (
            <TarjetaGrupo key={g.id} g={g} nombre={nombres.get(g.id) ?? null} indice={i} color={RAMPA[Math.min(i, RAMPA.length - 1)]} />
          ))}
        </div>
      </Seccion>

      <Seccion id="reporte-base" titulo="Toda tu base, sin que falte nadie">
        <div className="grid gap-3 sm:grid-cols-3">
          <Dato k="compraron" v={num(r.compraron)} />
          <Dato k="se registraron y nunca compraron" v={num(r.nuncaCompraron)} />
          <Dato k="personas en total" v={`= ${num(r.personas)}`} />
        </div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">De los que compraron</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Dato k={`con celular (${num(r.llegada.conCelular)})`} v={`${num(r.llegada.pctCelular)}%`} />
          <Dato k={`con mail (${num(r.llegada.conMail)})`} v={`${num(r.llegada.pctMail)}%`} />
          <Dato k="sin ningún contacto" v={num(r.llegada.sinContacto)} />
        </div>
      </Seccion>

      <Seccion id="reporte-leer" titulo="Cómo leer estos números">
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
          {r.comoLeer.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      </Seccion>
    </div>
  );
}
